// src/store/slices/ticketSlice.ts
import { createSlice, createAsyncThunk } from '@reduxjs/toolkit';
import apiService from '../services/apiService';
import { toast } from 'react-toastify';

const api = apiService();
const formApi = apiService('form');

// ————————————————————————————————
// Async actions for Tickets
// ————————————————————————————————

export const addTicketTag = createAsyncThunk(
  'tickets/addTicketTag',
  async ({ name }: { name: string }) => {
    const response = await api.post('/ticket/tag', { name });
    return response.data;
  }
);

export const fetchTicketTags = createAsyncThunk(
  'tickets/fetchTicketTags',
  async () => {
    const response = await api.get('/ticket/tag');
    return response.data;
  }
);

export const addTicket = createAsyncThunk(
  'tickets/addTicket',
  async (ticketData: FormData) => {
    const response = await formApi.post('/ticket', ticketData);
    return response.data;
  }
);

export const fetchMyTickets = createAsyncThunk(
  'tickets/fetchMyTickets',
  async (params: {
    status?: string;
    page?: number;
    limit?: number;
    search?: string;
  } = {}) => {
    const response = await api.get('/ticket/me', { params });
    return response.data;
  }
);

export const fetchTickets = createAsyncThunk(
  'tickets/fetchTickets',
  async (params: {
    page?: number;
    limit?: number;
    status?: string;
    search?: string;
  } = {}) => {
    const response = await api.get('/ticket', { params });
    return response.data;
  }
);

export const replyTicket = createAsyncThunk(
  'tickets/replyTicket',
  async ({ ticketId, formData }: { ticketId: string; formData: FormData }) => {
    const response = await formApi.post(`/ticket/${ticketId}`, formData);
    return response.data;
  }
);

export const updateTicketStatus = createAsyncThunk(
  'tickets/updateTicketStatus',
  async ({ ticketId, status }: { ticketId: string; status: string }) => {
    const response = await api.patch(`/ticket/status/${ticketId}`, { status });
    if (response.status === 200) {
      toast.success('Ticket status updated');
    } else {
      toast.error('Failed to update ticket status');
    }
    return response.data;
  }
);

export const fetchTicketById = createAsyncThunk(
  'tickets/fetchTicketById',
  async (ticketId: string) => {
    const response = await api.get(`/ticket/${ticketId}`);
    return response.data;
  }
);

// ————————————————————————————————
// State and Slice
// ————————————————————————————————

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  itemsPerPage: number;
  totalItems: number;
}

interface TicketState {
  tags: string[];
  list: any[];
  myList: any[];
  current?: any;
  pagination?: PaginationInfo;
  status: 'idle' | 'loading' | 'succeeded' | 'failed';
  error: string | null;
}

const initialState: TicketState = {
  tags: [],
  list: [],
  myList: [],
  current: null,
  pagination: undefined,
  status: 'idle',
  error: null,
};

const ticketSlice = createSlice({
  name: 'tickets',
  initialState,
  reducers: {},
  extraReducers: (builder) => {
    // addTicketTag
    builder
      .addCase(addTicketTag.fulfilled, (state, action) => {
        state.tags.push(action.payload);
      })
      .addCase(addTicketTag.rejected, (state, action) => {
        toast.error('Failed to add ticket tag');
        state.error = action.error.message ?? null;
      });

    // fetchTicketTags
    builder
      .addCase(fetchTicketTags.fulfilled, (state, action) => {
        state.tags = action.payload;
      })
      .addCase(fetchTicketTags.rejected, (state, action) => {
        toast.error('Failed to load ticket tags');
        state.error = action.error.message ?? null;
      });

    // addTicket
    builder
      .addCase(addTicket.fulfilled, (state, action) => {
        state.list.unshift(action.payload);
      })
      .addCase(addTicket.rejected, (state, action) => {
        toast.error('Failed to create ticket');
        state.error = action.error.message ?? null;
      });

    // fetchMyTickets
    builder
      .addCase(fetchMyTickets.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchMyTickets.fulfilled, (state, action) => {
        state.myList = action.payload.data ?? action.payload;
        state.pagination = action.payload.pagination ?? null;
        state.status = 'succeeded';
      })
      .addCase(fetchMyTickets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      });

    // fetchTickets
    builder
      .addCase(fetchTickets.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchTickets.fulfilled, (state, action) => {
        state.list = action.payload.data ?? action.payload;
        state.pagination = action.payload.pagination ?? null;
        state.status = 'succeeded';
      })
      .addCase(fetchTickets.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      });

    // replyTicket
    builder
      .addCase(replyTicket.fulfilled, (state, action) => {
        // update in list or current if needed
      })
      .addCase(replyTicket.rejected, (state, action) => {
        toast.error('Failed to send reply');
        state.error = action.error.message ?? null;
      });

    // updateTicketStatus
    builder
      .addCase(updateTicketStatus.fulfilled, (state, action) => {
        // update status in list/current
      })
      .addCase(updateTicketStatus.rejected, (state, action) => {
        state.error = action.error.message ?? null;
      });

    // fetchTicketById
    builder
      .addCase(fetchTicketById.pending, (state) => { state.status = 'loading'; })
      .addCase(fetchTicketById.fulfilled, (state, action) => {
        state.current = action.payload;
        state.status = 'succeeded';
      })
      .addCase(fetchTicketById.rejected, (state, action) => {
        state.status = 'failed';
        state.error = action.error.message ?? null;
      });
  },
});

export default ticketSlice.reducer;
